import React, { useState } from 'react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { saveSubmission } from '../mock';
import { Send, Loader2 } from 'lucide-react';

const ContactForm = () => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: '',
  });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validate = () => {
    const next = {};
    if (!form.firstName.trim()) next.firstName = 'First name is required';
    if (!form.lastName.trim()) next.lastName = 'Last name is required';
    if (!form.email.trim()) {
      next.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      next.email = 'Please enter a valid email';
    }
    if (!form.message.trim()) next.message = 'Message is required';
    else if (form.message.trim().length < 10)
      next.message = 'Message should be at least 10 characters';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    // Simulate network delay for a realistic feel
    await new Promise((r) => setTimeout(r, 700));
    try {
      saveSubmission(form);
      toast({
        title: 'Message sent',
        description: "Thank you for reaching out. We'll get back to you shortly.",
      });
      setForm({ firstName: '', lastName: '', email: '', message: '' });
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: 'Please try again or email us directly.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-10 pt-10 border-t border-[#e8e4da]">
      <div className="text-center mb-8">
        <h2 className="font-serif text-2xl md:text-3xl tracking-wide text-[#1f1d1a]">
          Get in Touch
        </h2>
        <div className="mx-auto mt-3 w-10 h-px bg-[#b8a88a]" />
        <p className="mt-4 text-sm md:text-base text-[#5a564e] max-w-md mx-auto leading-relaxed">
          Have a question about a past order or membership? Send us a message and
          we’ll respond as soon as possible.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5" noValidate>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="space-y-2">
            <Label
              htmlFor="firstName"
              className="text-xs uppercase tracking-widest text-[#3a3832] font-semibold"
            >
              First Name
            </Label>
            <Input
              id="firstName"
              name="firstName"
              value={form.firstName}
              onChange={handleChange}
              placeholder="Jane"
              className="bg-white border-[#d9d3c4] focus-visible:ring-[#b8a88a] focus-visible:ring-offset-0 h-11 rounded-none text-[#1f1d1a]"
              autoComplete="given-name"
            />
            {errors.firstName && (
              <p className="text-xs text-[#a33a2a]">{errors.firstName}</p>
            )}
          </div>
          <div className="space-y-2">
            <Label
              htmlFor="lastName"
              className="text-xs uppercase tracking-widest text-[#3a3832] font-semibold"
            >
              Last Name
            </Label>
            <Input
              id="lastName"
              name="lastName"
              value={form.lastName}
              onChange={handleChange}
              placeholder="Doe"
              className="bg-white border-[#d9d3c4] focus-visible:ring-[#b8a88a] focus-visible:ring-offset-0 h-11 rounded-none text-[#1f1d1a]"
              autoComplete="family-name"
            />
            {errors.lastName && (
              <p className="text-xs text-[#a33a2a]">{errors.lastName}</p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label
            htmlFor="email"
            className="text-xs uppercase tracking-widest text-[#3a3832] font-semibold"
          >
            Email
          </Label>
          <Input
            id="email"
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            placeholder="you@example.com"
            className="bg-white border-[#d9d3c4] focus-visible:ring-[#b8a88a] focus-visible:ring-offset-0 h-11 rounded-none text-[#1f1d1a]"
            autoComplete="email"
          />
          {errors.email && (
            <p className="text-xs text-[#a33a2a]">{errors.email}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label
            htmlFor="message"
            className="text-xs uppercase tracking-widest text-[#3a3832] font-semibold"
          >
            Message
          </Label>
          <Textarea
            id="message"
            name="message"
            value={form.message}
            onChange={handleChange}
            placeholder="How can we help you?"
            rows={5}
            className="bg-white border-[#d9d3c4] focus-visible:ring-[#b8a88a] focus-visible:ring-offset-0 rounded-none text-[#1f1d1a] resize-none"
          />
          {errors.message && (
            <p className="text-xs text-[#a33a2a]">{errors.message}</p>
          )}
        </div>

        <div className="pt-2 flex justify-center">
          <Button
            type="submit"
            disabled={submitting}
            className="group bg-[#1f1d1a] hover:bg-[#3a3832] text-white rounded-none px-10 h-12 text-xs uppercase tracking-[0.2em] font-semibold transition-colors duration-200 disabled:opacity-70"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending
              </>
            ) : (
              <>
                Send Message
                <Send className="ml-2 h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
